import type { ListingMenuKind } from '@pulsify/shared/dto'
import { BookOpen, Pencil, RefreshCw, Trash2, Utensils } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import * as m from '~/paraglide/messages'
import { DESTRUCTIVE_OUTLINE_BUTTON } from '~/shared/styles/buttons'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~/shared/ui/alert-dialog'
import { Badge } from '~/shared/ui/badge'
import { Button } from '~/shared/ui/button'
import { Card } from '~/shared/ui/card'
import { formatMonday, formatUpdatedAt, formatWeekdays } from '../lib/menu-format'
import type { MenuSummary } from '../lib/menu-summary'

function dishCountLabel(count: number): string {
  return count === 1 ? m.menuDishCountOne() : m.menuDishCount({ count })
}

function sectionCountLabel(count: number): string {
  return count === 1 ? m.menuSectionCountOne() : m.menuSectionCount({ count })
}

function SummaryLines({ summary, locale }: { summary: MenuSummary; locale: string }) {
  if (summary.kind === 'lunch') {
    return (
      <>
        <p className="font-medium text-foreground">
          {summary.weekOf
            ? m.menuWeekOf({ date: formatMonday(summary.weekOf, locale) })
            : m.menuEveryWeek()}
        </p>
        <p className="text-muted-foreground text-sm">
          {formatWeekdays(summary.weekdays, locale)} · {dishCountLabel(summary.dishCount)}
        </p>
        {!summary.visibleToday && (
          <p className="text-brand-subtle-foreground text-sm">{m.menuLunchPast()}</p>
        )}
        <p className="text-muted-foreground text-xs">
          {m.menuUpdatedAt({ date: formatUpdatedAt(summary.updatedAt, locale) })}
        </p>
      </>
    )
  }
  return (
    <>
      <p className="font-medium text-foreground">
        {sectionCountLabel(summary.sectionTitles.length)} · {dishCountLabel(summary.dishCount)}
      </p>
      <p className="line-clamp-2 text-muted-foreground text-sm">
        {summary.sectionTitles.join(', ')}
      </p>
      <p className="text-muted-foreground text-xs">
        {m.menuUpdatedAt({ date: formatUpdatedAt(summary.updatedAt, locale) })}
      </p>
    </>
  )
}

interface MenuCardProps {
  kind: ListingMenuKind
  summary: MenuSummary | null
  locale: string
  removing: boolean
  onRemove: () => void
  onEdit?: () => void
  onRescan?: () => void
  onWriteYourself?: () => void
  /** The drop zone shown while the card is empty. */
  upload?: ReactNode
}

export function MenuCard({
  kind,
  summary,
  locale,
  removing,
  onRemove,
  onEdit,
  onRescan,
  onWriteYourself,
  upload,
}: MenuCardProps) {
  const [confirming, setConfirming] = useState(false)
  const Icon = kind === 'lunch' ? Utensils : BookOpen
  const hidden = summary?.kind === 'lunch' && !summary.visibleToday

  return (
    <Card className="flex flex-col gap-5 p-6">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-brand-subtle text-brand-subtle-foreground">
            <Icon className="size-5" aria-hidden />
          </span>
          <h2 className="font-semibold font-serif text-foreground text-xl tracking-tight">
            {kind === 'lunch' ? m.menuLunchTitle() : m.menuFullTitle()}
          </h2>
        </div>
        {summary && (
          <Badge variant={hidden ? 'secondary' : 'default'}>
            {hidden ? m.menuHiddenBadge() : m.menuVisibleBadge()}
          </Badge>
        )}
      </div>

      {summary ? (
        <>
          <div className="flex flex-col gap-1">
            <SummaryLines summary={summary} locale={locale} />
          </div>
          <div className="flex flex-wrap gap-2">
            {onRescan && (
              <Button type="button" variant="outline" size="sm" onClick={onRescan}>
                <RefreshCw />
                {m.menuRescan()}
              </Button>
            )}
            {onEdit && (
              <Button type="button" variant="outline" size="sm" onClick={onEdit}>
                <Pencil />
                {m.edit()}
              </Button>
            )}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={removing}
              onClick={() => setConfirming(true)}
              className="text-muted-foreground hover:text-destructive"
            >
              <Trash2 />
              {m.menuRemove()}
            </Button>
          </div>
        </>
      ) : (
        <>
          <p className="text-muted-foreground text-sm">
            {kind === 'lunch' ? m.menuLunchEmpty() : m.menuFullEmpty()}
          </p>
          {upload}
          {onWriteYourself && (
            <Button type="button" variant="outline" className="w-fit" onClick={onWriteYourself}>
              <Pencil />
              {m.menuWriteYourself()}
            </Button>
          )}
        </>
      )}

      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {kind === 'lunch' ? m.menuRemoveLunchTitle() : m.menuRemoveFullTitle()}
            </AlertDialogTitle>
            <AlertDialogDescription>{m.menuRemoveDescription()}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{m.cancel()}</AlertDialogCancel>
            <AlertDialogAction className={DESTRUCTIVE_OUTLINE_BUTTON} onClick={onRemove}>
              {m.menuRemove()}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  )
}
